// @ts-nocheck
import React, { useCallback, useMemo } from 'react';
import { IconButton, InputAdornment, TextField, Tooltip } from '@mui/material';
import PropTypes from 'prop-types';
import LockIcon from '@mui/icons-material/Lock';

import { ReactComponent as CalculationIcon } from '../../../../assets/images/CalculationIcon.svg';

import { getErrorHelperText } from '../../../../utils/Utils';

export const INPUT_LABEL_PROPS = {
  shrink: true,
  sx: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    width: '100%',
  },
};

/**
 * Text field component for editing text attributes
 * @param {Object} props - Component props
 * @param {string} props.label - Field label
 * @param {string|number} props.value - Current field value
 * @param {Function} props.onChange - Function to handle value changes
 * @param {Object} props.attribute - Attribute configuration object
 * @param {Object} props.entityDetailsFormikForm - Formik form instance
 * @param {string} props.titleLabel - Title label for the field
 * @returns {React.ReactElement} The TextField component
 */
const TextFieldComponent = ({
  label,
  value,
  onChange,
  attribute,
  entityDetailsFormikForm,
  titleLabel,
  required = false,
  handleOpenDerivedModal,
  hideHeader,
  isLoadingConstraints = false,
  configColumn = false,
}) => {
  const isDisabled = !attribute?.is_editable;
  const isDerived = hideHeader
    ? attribute?.is_derived && attribute?.permission === 'manage'
    : attribute?.is_derived;

  const currentValue = useMemo(
    () => (value === undefined || value === null ? '' : String(value)),
    [value]
  );

  const errorHelperText = useMemo(
    () =>
      getErrorHelperText(
        entityDetailsFormikForm?.errors?.[attribute?.id],
        entityDetailsFormikForm?.touched?.[attribute?.id]
      ),
    [
      entityDetailsFormikForm?.errors,
      entityDetailsFormikForm?.touched,
      attribute?.id,
    ]
  );

  const hasError = useMemo(() => Boolean(errorHelperText), [errorHelperText]);

  const helperText = useMemo(() => errorHelperText || '', [errorHelperText]);

  /**
   * Handles input change events
   * @function
   * @param {React.ChangeEvent<HTMLInputElement>} e - The change event
   * @returns {void}
   */
  const handleChange = useCallback(
    (e) => {
      onChange(e.target.value);
    },
    [onChange]
  );

  const handleClickAttribute = useCallback(
    (e) => {
      e.stopPropagation();
      handleOpenDerivedModal(attribute);
    },
    [handleOpenDerivedModal, attribute]
  );

  const inputPropsConfig = useMemo(() => {
    let endAdornment = null;

    if (isDerived) {
      endAdornment = (
        <InputAdornment position="end">
          <Tooltip
            title={
              !attribute?.show_calculation_popUp
                ? 'Enable the calculation popup to calculate this attribute.'
                : `Calculate ${attribute?.label}`
            }
          >
            <IconButton
              sx={{ p: 0 }}
              className="disabled-svg-icon"
              onClick={handleClickAttribute}
              disabled={!attribute?.show_calculation_popUp}
            >
              <CalculationIcon />
            </IconButton>
          </Tooltip>
        </InputAdornment>
      );
    } else if (!attribute?.is_editable) {
      endAdornment = (
        <InputAdornment position="end">
          <Tooltip title="Field is locked or not editable">
            <LockIcon />
          </Tooltip>
        </InputAdornment>
      );
    }

    return { endAdornment };
  }, [attribute?.is_derived, attribute?.is_editable]);

  const handleKeyDown = useCallback(
    (e) => {
      if (configColumn && e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
      }
    },
    [configColumn]
  );

  return (
    <TextField
      {...entityDetailsFormikForm?.getFieldProps(attribute?.id)}
      type={configColumn ? undefined : 'text'}
      multiline={configColumn}
      fullWidth
      title={titleLabel}
      label={label}
      placeholder={isDerived ? '-' : 'Enter a value'}
      value={currentValue}
      disabled={isDisabled || isDerived || isLoadingConstraints}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      variant="outlined"
      size="medium"
      spellCheck
      required={required}
      InputProps={inputPropsConfig}
      error={hasError}
      helperText={helperText}
      InputLabelProps={INPUT_LABEL_PROPS}
    />
  );
};

TextFieldComponent.propTypes = {
  label: PropTypes.string.isRequired,
  titleLabel: PropTypes.string.isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  required: PropTypes.bool,
  attribute: PropTypes.shape({
    name: PropTypes.string,
    data_type: PropTypes.string,
    is_editable: PropTypes.bool,
    is_derived: PropTypes.bool,
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    label: PropTypes.string,
    permission: PropTypes.string,
    show_calculation_popUp: PropTypes.bool,
  }).isRequired,
  entityDetailsFormikForm: PropTypes.shape({
    getFieldProps: PropTypes.func.isRequired,
    errors: PropTypes.objectOf(PropTypes.string).isRequired,
    touched: PropTypes.objectOf(PropTypes.bool).isRequired,
  }).isRequired,
  handleOpenDerivedModal: PropTypes.func,
  hideHeader: PropTypes.bool.isRequired,
  isLoadingConstraints: PropTypes.bool,
  configColumn: PropTypes.bool,
};

export default React.memo(TextFieldComponent);
